// qrPrintAncestors.js

import { normalizeAncestryPath } from 'utilities/nodeAncestry';

const DEFAULT_TITLE_SIZE_PX = 16;
const DEFAULT_FIELD_SIZE_PX = 13;
const ROOT_ANCESTOR_SIZE_PX = 22;
const SIZE_STEP_PX = 3;
const MIN_ANCESTOR_SIZE_PX = 14;
const MIN_TITLE_SIZE_PX = 13;
const MIN_FIELD_SIZE_PX = 11;

/**
 * buildQrPrintAncestorLines
 *
 * Maps stamped ancestryPath entries to printable title lines using
 * hydrated breadcrumb fields (same source as StructuralAncestryTrail).
 *
 * @param {*} ancestryPath - Node ancestryPath ({ type, key }[]).
 * @param {Map<string, { title?: string, type?: string }>|null|undefined} titleByKey
 * @returns {{ key: string, type: string, title: string }[]}
 */
export function buildQrPrintAncestorLines(ancestryPath, titleByKey) {
  const path = normalizeAncestryPath(ancestryPath);
  if (!path.length) return [];

  const map = titleByKey instanceof Map ? titleByKey : null;

  return path
    .map((entry) => {
      const hydrated = map?.get(entry.key);
      const title = String(hydrated?.title || '').trim();
      if (!title) return null;
      return {
        key: entry.key,
        type: entry.type,
        title,
      };
    })
    .filter(Boolean);
}

/**
 * getQrPrintTypography
 *
 * Progressive sizes: root ancestor largest, then each step down the tree,
 * then the item title, then field values (smallest).
 *
 * When there are no ancestors, keeps the existing title/field defaults.
 *
 * @param {number} ancestorCount - Number of ancestor title lines to print.
 * @returns {{
 *   ancestorSizesPx: number[],
 *   titleSizePx: number,
 *   fieldSizePx: number,
 * }}
 */
export function getQrPrintTypography(ancestorCount) {
  const count = Math.max(0, Number(ancestorCount) || 0);
  if (count <= 0) {
    return {
      ancestorSizesPx: [],
      titleSizePx: DEFAULT_TITLE_SIZE_PX,
      fieldSizePx: DEFAULT_FIELD_SIZE_PX,
    };
  }

  const ancestorSizesPx = Array.from({ length: count }, (_, index) => (
    Math.max(MIN_ANCESTOR_SIZE_PX, ROOT_ANCESTOR_SIZE_PX - (index * SIZE_STEP_PX))
  ));
  const titleSizePx = Math.max(
    MIN_TITLE_SIZE_PX,
    ROOT_ANCESTOR_SIZE_PX - (count * SIZE_STEP_PX),
  );
  const fieldSizePx = Math.max(MIN_FIELD_SIZE_PX, titleSizePx - 2);

  return {
    ancestorSizesPx,
    titleSizePx,
    fieldSizePx,
  };
}
